(() => {
  'use strict';

  const DESKTOP = matchMedia('(min-width: 750px)');
  const MAX_ROWS = 3;
  const REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)').matches;

  function swatchItems(container) {
    return [...container.children].filter((el) => el.classList.contains('rbd-color-swatch'));
  }

  function measureFullHeight(swatches) {
    const previousMax = swatches.style.maxHeight;
    swatches.style.maxHeight = 'none';
    const height = swatches.scrollHeight;
    swatches.style.maxHeight = previousMax;
    return height;
  }

  function cutoffHeightForRows(container, maxRows) {
    const items = swatchItems(container);
    if (!items.length) return container.scrollHeight;

    const containerTop = container.getBoundingClientRect().top;
    const rowTops = [];

    items.forEach((item) => {
      const top = Math.round(item.getBoundingClientRect().top - containerTop);
      if (!rowTops.some((value) => Math.abs(value - top) <= 1)) {
        rowTops.push(top);
      }
    });

    rowTops.sort((a, b) => a - b);
    if (rowTops.length <= maxRows) return null;

    const lastIncludedRowTop = rowTops[maxRows - 1];
    let bottom = 0;

    items.forEach((item) => {
      const top = Math.round(item.getBoundingClientRect().top - containerTop);
      if (top <= lastIncludedRowTop + 1) {
        const itemBottom = item.getBoundingClientRect().bottom - containerTop;
        bottom = Math.max(bottom, itemBottom);
      }
    });

    return Math.ceil(bottom);
  }

  function initGroup(group) {
    if (group.dataset.rbdLayoutBound === 'true') return;

    const swatches = group.querySelector('[data-rbd-color-swatches]');
    const viewMore = group.querySelector('[data-rbd-color-view-more]');
    if (!swatches) return;

    group.dataset.rbdLayoutBound = 'true';

    const sync = () => {
      if (!DESKTOP.matches) {
        swatches.style.maxHeight = '';
        group.classList.remove('is-swatches-expanded', 'is-swatches-collapsed');
        if (viewMore) viewMore.hidden = true;
        return;
      }

      if (group.classList.contains('is-swatches-expanded')) {
        if (swatches.style.maxHeight !== 'none') {
          swatches.style.maxHeight = `${measureFullHeight(swatches)}px`;
        }
        return;
      }

      const fullHeight = measureFullHeight(swatches);
      const cutoff = cutoffHeightForRows(swatches, MAX_ROWS);

      if (cutoff == null || fullHeight <= cutoff + 2) {
        swatches.style.maxHeight = '';
        group.classList.remove('is-swatches-collapsed');
        if (viewMore) viewMore.hidden = true;
        return;
      }

      group.classList.add('is-swatches-collapsed');
      swatches.style.maxHeight = `${cutoff}px`;
      if (viewMore) viewMore.hidden = false;
    };

    viewMore?.addEventListener('click', () => {
      const fullHeight = measureFullHeight(swatches);
      group.classList.add('is-swatches-expanded');
      group.classList.remove('is-swatches-collapsed');
      viewMore.hidden = true;

      if (REDUCED_MOTION) {
        swatches.style.maxHeight = 'none';
        return;
      }

      swatches.style.maxHeight = `${fullHeight}px`;
      const onTransitionEnd = (event) => {
        if (event.propertyName !== 'max-height') return;
        swatches.removeEventListener('transitionend', onTransitionEnd);
        swatches.style.maxHeight = 'none';
      };
      swatches.addEventListener('transitionend', onTransitionEnd);
    });

    DESKTOP.addEventListener('change', sync);
    window.addEventListener('load', sync);
    window.addEventListener('resize', sync);
    document.fonts?.ready?.then(sync);

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(sync);
      });
      observer.observe(swatches);
      swatchItems(swatches).forEach((item) => {
        item.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', sync, { once: true });
        });
      });
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(sync);
    });
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-rbd-color-group]').forEach(initGroup);
  }

  initAll();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('[data-rbd-color-group]')) {
      initAll(event.target);
    }
  });
})();
